import { AdditionalDocument } from '$types/candidates';
import { useMemo, useState } from 'react';
import { ReactComponent as Add } from '$icons/uncolored/Add.svg';
import {
  GetCertificationsOutput,
  useGetCertificationsQuery,
} from '$store/modules/certifications';
import { useTranslation } from 'react-i18next';
import CandidateUpsertCertification from './CandidateUpsertCertification';
import CandidateCertificationsViewer from './CandidateCertificationsViewer';

const CandidateCertifications = ({
  certificationFiles,
  candidateId,
  certificationFileCategoryId,
}: {
  certificationFiles: AdditionalDocument[];
  candidateId: number;
  certificationFileCategoryId: number;
}) => {
  const [t] = useTranslation('global');
  const [mode, setMode] = useState<'view' | 'upsert'>('view');
  const [selectedCertification, setSelectedCertification] = useState<
    GetCertificationsOutput[number] | undefined
  >();
  const { data: candidateCertifications } =
    useGetCertificationsQuery(candidateId);

  const untiedCertificationFiles = useMemo(() => {
    const certificationFilesWithoutCertification =
      (certificationFiles &&
        candidateCertifications &&
        certificationFiles.filter(
          (singleCertificationFile) =>
            !candidateCertifications?.find(
              (singleCandidateCertification) =>
                singleCandidateCertification.FileId ===
                singleCertificationFile.Id
            )
        )) ||
      [];
    const selectedCertificationFile =
      certificationFiles &&
      selectedCertification &&
      certificationFiles.find(
        (singleCertificationFile) =>
          singleCertificationFile.Id === selectedCertification.FileId
      );

    return [
      ...certificationFilesWithoutCertification,
      ...(selectedCertificationFile ? [selectedCertificationFile] : []),
    ];
  }, [certificationFiles, candidateCertifications, selectedCertification]);

  return (
    <div className="bg-white rounded-sm p-6">
      <div className="flex flex-row justify-between items-center mb-4">
        <h1 className="font-semibold text-xl">
          {t('CandidateCertifications.title')}
        </h1>
        <button
          type="button"
          className="flex flex-row gap-2"
          onClick={() => setMode('upsert')}
          title="Add"
        >
          <Add className="fill-black" />
        </button>
      </div>
      {mode === 'view' && (
        <CandidateCertificationsViewer
          candidateCertifications={candidateCertifications}
          certificationFiles={certificationFiles}
          onEdit={(certification) => {
            setMode('upsert');
            setSelectedCertification(certification);
          }}
        />
      )}
      {mode === 'upsert' && (
        <CandidateUpsertCertification
          certificationFiles={untiedCertificationFiles}
          onComplete={() => {
            setMode('view');
            setSelectedCertification(undefined);
          }}
          candidateId={candidateId}
          certificationFileCategoryId={certificationFileCategoryId}
          selectedCertification={selectedCertification}
        />
      )}
    </div>
  );
};

export default CandidateCertifications;
